/**
 * SharedFooter — the canonical WeKIT™ footer.
 *
 * PORTABILITY CONTRACT (do not break):
 *  - Zero project-specific imports. Only react + react-router-dom + lucide-react.
 *  - No shadcn, no design tokens, no @/config, no @/lib helpers.
 *  - All copy/links/socials are inline constants below so this file can be
 *    copy-pasted UNCHANGED into every WeKIT™ project (main site, NRECT app,
 *    College Discovery app, mentor app, etc.).
 *
 * To update branding across the ecosystem: edit this file, then sync it to
 * every project (or import via the cross-project copy tooling).
 *
 * Version: 1.0.0  (bump when you change anything users see)
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ChevronRight, Download, Building2, ArrowRight } from 'lucide-react';
import wekitLogoAsset from '@/assets/wekit-logo.png.asset.json';

// ---------- Inline constants (single source of truth) ----------

const WEKIT_LOGO_URL = wekitLogoAsset.url;
// Fallback to lovable-uploads if the asset URL isn't available in a given project:
const WEKIT_LOGO_FALLBACK = wekitLogoAsset.url;

const SOCIAL_LINKS = [
  { key: 'linkedin', label: 'LinkedIn', href: 'https://www.linkedin.com/company/wekitai' },
  { key: 'instagram', label: 'Instagram', href: 'https://www.instagram.com/we.kit__you' },
  { key: 'facebook', label: 'Facebook', href: 'https://www.facebook.com/wekitorg' },
  { key: 'x', label: 'X (Twitter)', href: 'https://x.com/_we_kit_?s=20' },
  { key: 'youtube', label: 'YouTube', href: 'https://youtube.com/@wekitmentoring' },
  { key: 'substack', label: 'Substack', href: 'https://wekit.substack.com' },
] as const;

// ---------- Inline link validation (portability: no @/ imports) ----------
// Validates URL shape + per-platform host allowlist. Broken entries are
// disabled in the UI and logged to the console. Async reachability probe
// (dev-only) marks links amber if the platform can't be reached.

type SocialKey = (typeof SOCIAL_LINKS)[number]['key'];
type HealthState = 'ok' | 'checking' | 'unreachable' | 'invalid' | 'idle';

const HOST_ALLOWLIST: Record<SocialKey, string[]> = {
  linkedin: ['linkedin.com', 'www.linkedin.com'],
  instagram: ['instagram.com', 'www.instagram.com'],
  facebook: ['facebook.com', 'www.facebook.com', 'fb.com', 'www.fb.com'],
  x: ['x.com', 'www.x.com', 'twitter.com', 'www.twitter.com'],
  youtube: ['youtube.com', 'www.youtube.com', 'youtu.be'],
  substack: ['substack.com'],
};

const validateSocial = (s: { key: SocialKey; href: string }): boolean => {
  try {
    const u = new URL(s.href);
    if (u.protocol !== 'https:') return false;
    const host = u.hostname.toLowerCase();
    const allowed = HOST_ALLOWLIST[s.key] || [];
    return allowed.some(
      (h) => host === h || host.endsWith(`.${h}`) || host === h.replace(/^www\./, '')
    );
  } catch {
    return false;
  }
};

const INVALID_KEYS = new Set<SocialKey>(
  SOCIAL_LINKS.filter((s) => !validateSocial(s)).map((s) => s.key)
);

if (typeof window !== 'undefined' && INVALID_KEYS.size > 0) {
  // eslint-disable-next-line no-console
  console.error(
    '[SharedFooter] Invalid social links — disabled in footer:',
    SOCIAL_LINKS.filter((s) => INVALID_KEYS.has(s.key))
  );
}

const REGISTERED_OFFICE =
  '91/C, 3rd Floor, M.N. Ramaiah Layout, Kammanahalli Extn, St Thomas Town P.O., Bangalore North, Bangalore – 560084, India.';
const CIN = 'U85499KA2024PTC195031';
const COMPANY_LEGAL_NAME = 'We-KIT International Pvt Limited';
const BRAND_TAGLINE = 'Rooting YOU in YOUr Purpose';
const BRAND_ACRONYM = 'WeKIT™ = Wot Kareer Is it?';
const MAIN_SITE = 'https://www.wekitmentoring.com';

// ---------- Brand SVG icons (full-color, inline for portability) ----------

type IconProps = { size?: number; className?: string };

const InstagramBrand = ({ size = 22, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <defs>
      <radialGradient id="ig-grad" cx="30%" cy="107%" r="150%">
        <stop offset="0%" stopColor="#fdf497" />
        <stop offset="5%" stopColor="#fdf497" />
        <stop offset="45%" stopColor="#fd5949" />
        <stop offset="60%" stopColor="#d6249f" />
        <stop offset="90%" stopColor="#285AEB" />
      </radialGradient>
    </defs>
    <rect x="2" y="2" width="20" height="20" rx="5.5" fill="url(#ig-grad)" />
    <circle cx="12" cy="12" r="4.2" fill="none" stroke="#fff" strokeWidth="1.8" />
    <circle cx="17.3" cy="6.7" r="1.2" fill="#fff" />
  </svg>
);

const FacebookBrand = ({ size = 22, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <rect width="24" height="24" rx="5" fill="#1877F2" />
    <path d="M15.12 12.5H13v7H10v-7H8.5v-2.5H10V8.6c0-1.8 1.07-3 3.04-3 .94 0 1.74.07 1.97.1v2.3h-1.35c-1.06 0-1.27.5-1.27 1.24V10h2.55l-.32 2.5Z" fill="#fff" />
  </svg>
);

const XBrand = ({ size = 22, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <rect width="24" height="24" rx="5" fill="#000" />
    <path d="M17.5 5h2.1l-4.6 5.26L20.5 19h-4.27l-3.34-4.37L8.94 19H6.83l4.92-5.63L6.2 5h4.38l3.02 3.99L17.5 5Zm-.75 12.6h1.17L9.36 6.32H8.1l8.65 11.28Z" fill="#fff" />
  </svg>
);

const YouTubeBrand = ({ size = 22, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <rect y="4" width="24" height="16" rx="4" fill="#FF0000" />
    <path d="M10 8.5v7l6-3.5-6-3.5Z" fill="#fff" />
  </svg>
);

const LinkedInBrand = ({ size = 22, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <rect width="24" height="24" rx="4" fill="#0A66C2" />
    <path d="M7.2 9.6H5V18h2.2V9.6Zm-1.1-3.5a1.27 1.27 0 1 0 0 2.55 1.27 1.27 0 0 0 0-2.55ZM19 18h-2.2v-4.1c0-.98-.02-2.24-1.37-2.24-1.37 0-1.58 1.07-1.58 2.17V18H11.6V9.6h2.1v1.15h.03c.3-.56 1.02-1.15 2.1-1.15 2.25 0 2.67 1.48 2.67 3.4V18H19Z" fill="#fff" />
  </svg>
);

const SubstackBrand = ({ size = 22, className }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <rect width="24" height="24" rx="4" fill="#FF6719" />
    <path d="M6.5 7h11v1.6h-11V7Zm0 3.2h11v1.6h-11v-1.6Zm0 3.2h11V19l-5.5-3-5.5 3v-5.6Z" fill="#fff" />
  </svg>
);

const SOCIAL_ICON: Record<string, (p: IconProps) => JSX.Element> = {
  instagram: InstagramBrand,
  facebook: FacebookBrand,
  x: XBrand,
  youtube: YouTubeBrand,
  linkedin: LinkedInBrand,
  substack: SubstackBrand,
};


// ---------- Types ----------

type NavGroup = { title?: string; items: { label: string; to: string; external?: boolean }[] };

export interface SharedFooterProps {
  /** Override the 3 link columns. Default = WeKIT™ main-site map. */
  navGroups?: NavGroup[];
  /** Hide nav columns entirely (useful on minimal apps like NRECT). */
  hideNav?: boolean;
  /** Optional banner row (e.g. NRECT partner line). */
  partnerLine?: React.ReactNode;
}

const DEFAULT_NAV: NavGroup[] = [
  {
    title: 'Explore',
    items: [
      { label: 'About', to: `${MAIN_SITE}/about`, external: true },
      { label: 'Programs', to: `${MAIN_SITE}/programs`, external: true },
      { label: 'Mentorship', to: `${MAIN_SITE}/mentorship`, external: true },
      { label: 'Courses', to: `${MAIN_SITE}/courses`, external: true },
      { label: 'Resources', to: `${MAIN_SITE}/resources`, external: true },
      { label: 'Start now', to: `${MAIN_SITE}/start-journey`, external: true },
    ],
  },
  {
    title: 'For you',
    items: [
      { label: 'Students', to: `${MAIN_SITE}/students`, external: true },
      { label: 'Parents', to: `${MAIN_SITE}/parents`, external: true },
      { label: 'Professionals', to: `${MAIN_SITE}/professionals`, external: true },
      { label: 'Schools', to: `${MAIN_SITE}/schools`, external: true },
      { label: 'NGOs', to: `${MAIN_SITE}/ngos`, external: true },
      { label: 'Enterprise', to: `${MAIN_SITE}/enterprise`, external: true },
    ],
  },
  {
    title: 'Company',
    items: [
      { label: 'Investors', to: `${MAIN_SITE}/investors`, external: true },
      { label: 'FAQs', to: '/faqs' },
      { label: 'Occupation standards', to: '/occupation-standards' },
      { label: 'Responsible AI', to: '/responsible-ai' },
      { label: 'Mentor Code', to: '/mentor-code' },
    ],
  },
  {
    title: 'Legal',
    items: [
      { label: 'Privacy', to: '/privacy' },
      { label: 'Terms', to: '/terms' },
      { label: 'Cookie Policy', to: '/cookies' },
      { label: 'Child Safety', to: '/child-safety' },
    ],
  },
];

// ---------- Component ----------

const SharedFooter = ({ navGroups = DEFAULT_NAV, hideNav = false, partnerLine }: SharedFooterProps) => {
  // Runtime reachability check (dev-only; enable in prod with ?checkSocials=1)
  const [health, setHealth] = useState<Record<string, HealthState>>(() => {
    const init: Record<string, HealthState> = {};
    SOCIAL_LINKS.forEach((s) => {
      init[s.key] = INVALID_KEYS.has(s.key) ? 'invalid' : 'idle';
    });
    return init;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const isDev =
      (typeof import.meta !== 'undefined' && (import.meta as any).env?.DEV) ||
      new URLSearchParams(window.location.search).has('checkSocials');
    if (!isDev) return;

    const CACHE_KEY = 'wekit:social-link-health:shared:v1';
    const TTL = 60 * 60 * 1000;
    let cache: Record<string, { status: HealthState; ts: number }> = {};
    try {
      cache = JSON.parse(window.sessionStorage.getItem(CACHE_KEY) || '{}');
    } catch {
      cache = {};
    }

    const toProbe: typeof SOCIAL_LINKS[number][] = [];
    const next: Record<string, HealthState> = {};
    const now = Date.now();
    SOCIAL_LINKS.forEach((s) => {
      if (INVALID_KEYS.has(s.key)) { next[s.key] = 'invalid'; return; }
      const c = cache[s.href];
      if (c && now - c.ts < TTL) next[s.key] = c.status;
      else { next[s.key] = 'checking'; toProbe.push(s); }
    });
    setHealth((prev) => ({ ...prev, ...next }));
    if (toProbe.length === 0) return;

    let cancelled = false;
    const probe = async (href: string) => {
      for (let i = 0; i < 2; i++) {
        const ac = new AbortController();
        const t = setTimeout(() => ac.abort(), 5000);
        try {
          await fetch(href, { method: 'HEAD', mode: 'no-cors', redirect: 'follow', signal: ac.signal });
          clearTimeout(t);
          return true;
        } catch {
          clearTimeout(t);
        }
      }
      return false;
    };

    (async () => {
      const updates: Record<string, HealthState> = {};
      const failed: string[] = [];
      await Promise.all(
        toProbe.map(async (s) => {
          const ok = await probe(s.href);
          const status: HealthState = ok ? 'ok' : 'unreachable';
          updates[s.key] = status;
          cache[s.href] = { status, ts: Date.now() };
          if (!ok) failed.push(`${s.label} (${s.href})`);
        })
      );
      if (cancelled) return;
      try { window.sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache)); } catch { /* ignore */ }
      setHealth((prev) => ({ ...prev, ...updates }));
      if (failed.length > 0) {
        // eslint-disable-next-line no-console
        console.warn('[SharedFooter] Social links may be unreachable:', failed);
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderLink = (item: { label: string; to: string; external?: boolean }) => {
    const cls =
      'block text-[hsl(0,0%,85%)] hover:text-white transition-colors';
    return item.external ? (
      <a key={item.label} href={item.to} target="_blank" rel="noopener noreferrer" className={cls}>
        {item.label}
      </a>
    ) : (
      <Link key={item.label} to={item.to} className={cls}>
        {item.label}
      </Link>
    );
  };

  return (
    <footer className="bg-[hsl(210,28%,10%)] text-[hsl(0,0%,75%)] font-sans">
      <div className="max-w-7xl mx-auto px-6 md:px-8">
        {/* Brand + CTAs — one compact row */}
        <div className="pt-10 pb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 bg-white p-1.5 flex items-center justify-center shrink-0">
              <img src={WEKIT_LOGO_FALLBACK || WEKIT_LOGO_URL} alt="WeKIT™ Logo" className="h-full w-full object-contain" loading="lazy" />
            </div>
            <div className="leading-tight">
              <p className="text-[hsl(0,0%,98%)] text-xl font-bold tracking-tight">
                WeKIT™ <span className="text-sm font-medium text-[hsl(0,0%,80%)]">= <span className="italic">Wot Kareer Is it?</span></span>
              </p>
              <p className="text-[hsl(45,80%,65%)] text-sm font-medium italic">{BRAND_TAGLINE}</p>
              <p className="mt-0.5 text-[hsl(0,0%,62%)] text-[10px] uppercase tracking-[0.18em] font-semibold">
                Powered by the WeKIT™ Human Development Intelligence Engine
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:w-[560px]">
            <Link to="/parent-guide" aria-label="For parents: download the free Parent Guide PDF"
              className="group min-h-12 rounded-2xl bg-gradient-to-br from-[hsl(24,95%,55%)] to-[hsl(14,90%,48%)] px-4 py-3 flex items-center gap-3 transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
              <Download className="h-4 w-4 text-white shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-white/85 text-[10px] uppercase tracking-[0.16em] font-semibold">For Parents</span>
                <span className="block text-white text-sm font-semibold leading-tight">Download the Parent Guide</span>
              </span>
              <ArrowRight className="h-4 w-4 text-white shrink-0 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link to="/institution" aria-label="For schools: partner with WeKIT Career Clarity 360"
              className="group min-h-12 rounded-2xl bg-gradient-to-br from-[hsl(158,64%,42%)] to-[hsl(172,66%,32%)] px-4 py-3 flex items-center gap-3 transition-transform hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70">
              <Building2 className="h-4 w-4 text-white shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-white/85 text-[10px] uppercase tracking-[0.16em] font-semibold">For Schools</span>
                <span className="block text-white text-sm font-semibold leading-tight">Institution Sign-Up</span>
              </span>
              <ArrowRight className="h-4 w-4 text-white shrink-0 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        {/* Nav + Contact */}
        {!hideNav && (
          <div className="border-t border-[hsl(210,20%,18%)] grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-6 py-8">
            {navGroups.map((g, i) => (
              <div key={i} className="space-y-2 text-sm">
                {g.title && (
                  <p className="text-[hsl(0,0%,60%)] uppercase tracking-wider text-[11px] font-semibold mb-3">{g.title}</p>
                )}
                {g.items.map(renderLink)}
              </div>
            ))}
            <div className="space-y-2 text-sm">
              <p className="text-[hsl(0,0%,60%)] uppercase tracking-wider text-[11px] font-semibold mb-3">Contact</p>
              <a href="mailto:hello@wekitmentoring.com" className="flex items-center text-[hsl(0,0%,95%)] font-medium hover:text-white">
                Get in Touch <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </a>
              <a href="mailto:support@wekitmentoring.com" className="flex items-center text-[hsl(0,0%,95%)] font-medium hover:text-white">
                Media Inquiries <ChevronRight className="ml-1 h-3.5 w-3.5" />
              </a>
              <p className="text-xs leading-relaxed text-[hsl(0,0%,62%)]">Partnerships, programs, press — we'd love to hear from you.</p>
            </div>
          </div>
        )}

        {/* Partner / NRECT line */}
        {(partnerLine ?? (
          <div className="border-t border-[hsl(210,20%,18%)] py-6 text-sm text-center md:text-left">
            <span className="text-[hsl(0,0%,60%)] tracking-wider uppercase text-[11px] mr-2">
              Social Impact Implementation Partner
            </span>
            <a
              href="https://www.napoleonrobertsfoundation.org"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[hsl(0,0%,95%)] font-medium hover:text-white transition-colors underline underline-offset-4"
            >
              NRECT Foundation
            </a>
            <span className="text-[hsl(0,0%,60%)]"> · Regd. Charitable Trust, Karnataka</span>
          </div>
        ))}

        {/* Stay in the Loop — compact social band */}
        <div className="border-t border-[hsl(210,20%,18%)] py-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center gap-4">
            <p className="text-[hsl(0,0%,98%)] text-lg font-semibold leading-tight text-center sm:text-right">
              Stay in the Loop
            </p>
            <ul
              role="list"
              aria-label="Social media"
              className="flex items-center gap-3 flex-wrap justify-center list-none p-0 m-0"
            >
              {/* Status-change announcer for assistive tech (dev probes etc.) */}
              <li className="sr-only" aria-live="polite" aria-atomic="true">
                {SOCIAL_LINKS.filter((s) => {
                  const st = health[s.key];
                  return st === 'invalid' || st === 'unreachable';
                })
                  .map((s) => `${s.label} ${health[s.key] === 'invalid' ? 'is unavailable' : 'may be unreachable'}.`)
                  .join(' ')}
              </li>
              {SOCIAL_LINKS.map((s) => {
                const Icon = SOCIAL_ICON[s.key];
                const status = health[s.key];
                const invalid = status === 'invalid';
                // Note: 'unreachable' from no-cors HEAD probes is unreliable for
                // social platforms (X, IG, etc. block them). Treat as healthy.
                const unreachable = false;
                const statusId = `social-status-${s.key}`;
                const baseCls =
                  'group relative h-12 w-12 inline-flex items-center justify-center rounded-2xl bg-white/[0.06] ring-1 ring-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-white hover:-translate-y-1 hover:shadow-[0_12px_28px_-8px_rgba(255,255,255,0.25)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d4af37] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(210,28%,10%)]';
                const stateCls = invalid
                  ? 'ring-2 ring-red-500/70 opacity-60 cursor-not-allowed hover:bg-white/[0.06] hover:translate-y-0 hover:shadow-none'
                  : '';
                const ariaLabel = invalid
                  ? `${s.label} link is unavailable`
                  : `Open ${s.label} in a new tab`;

                const iconNode = (
                  <>
                    <Icon size={26} className="transition-transform duration-300 group-hover:scale-110" />
                    {invalid && (
                      <span
                        aria-hidden="true"
                        className="absolute -top-1 -right-1 h-3 w-3 rounded-full ring-2 ring-[hsl(210,28%,10%)] bg-red-500"
                      />
                    )}
                    <span id={statusId} className="sr-only">
                      {invalid
                        ? 'Link unavailable due to configuration error.'
                        : unreachable
                        ? 'Warning: link may be unreachable.'
                        : ''}
                    </span>
                  </>
                );

                return (
                  <li key={s.key}>
                    {invalid ? (
                      <button
                        type="button"
                        disabled
                        aria-disabled="true"
                        aria-describedby={statusId}
                        aria-label={ariaLabel}
                        title={ariaLabel}
                        className={`${baseCls} ${stateCls}`}
                      >
                        {iconNode}
                      </button>
                    ) : (
                      <a
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        aria-label={ariaLabel}
                        aria-describedby={unreachable ? statusId : undefined}
                        title={ariaLabel}
                        className={`${baseCls} ${stateCls}`}
                      >
                        {iconNode}
                      </a>
                    )}
                  </li>
                );
              })}
              <li>
                <a
                  href="mailto:hello@wekitmentoring.com"
                  aria-label="Email WeKIT™ at hello@wekitmentoring.com"
                  className="group relative h-12 w-12 inline-flex items-center justify-center rounded-2xl bg-white/[0.06] text-white ring-1 ring-white/10 backdrop-blur-sm transition-all duration-300 hover:bg-[#d4af37] hover:text-black hover:-translate-y-1 hover:shadow-[0_12px_28px_-8px_rgba(212,175,55,0.4)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d4af37] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(210,28%,10%)]"
                >
                  <Mail size={22} className="transition-transform duration-300 group-hover:scale-110" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Data attribution — O*NET Web Services (USDOL/ETA terms of service) */}
        <div className="border-t border-[hsl(210,20%,18%)] py-6 flex flex-col sm:flex-row sm:items-center gap-3 text-[12px] leading-relaxed text-[hsl(0,0%,62%)]">
          <a
            href="https://services.onetcenter.org/"
            title="This site incorporates information from O*NET Web Services. Click to learn more."
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex shrink-0 items-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-[#d4af37]"
          >
            <img
              src="https://www.onetcenter.org/image/link/onet-in-it.svg"
              width={130}
              height={60}
              loading="lazy"
              style={{ width: 130, height: 60, border: 'none' }}
              alt="O*NET in-it — information from O*NET Web Services"
            />
          </a>
          <p>
            This site incorporates information from{' '}
            <a
              href="https://services.onetcenter.org/"
              target="_blank"
              rel="noreferrer noopener"
              className="text-[hsl(0,0%,82%)] underline underline-offset-2 hover:text-[hsl(0,0%,95%)]"
            >
              O*NET Web Services
            </a>{' '}
            by the U.S. Department of Labor, Employment and Training Administration (USDOL/ETA).
            O*NET<sup>®</sup> is a trademark of USDOL/ETA.
          </p>
        </div>

        {/* Registered Office — small & elegant */}
        <div className="border-t border-[hsl(210,20%,18%)] py-6 text-[12px] leading-relaxed text-[hsl(0,0%,62%)] space-y-0.5">
          <p>Registered Office: {REGISTERED_OFFICE}</p>
          <p>Corporate Identification Number (CIN): {CIN}</p>
        </div>


        {/* Legal bottom bar */}
        <div className="border-t border-[hsl(210,20%,18%)] py-6 flex flex-col md:flex-row items-center justify-between gap-4 text-sm">
          <span className="text-[hsl(0,0%,65%)]">
            © {new Date().getFullYear()} {COMPANY_LEGAL_NAME}. All rights reserved.
          </span>
          <div className="flex items-center gap-5">
            <a
              href="/terms"
              className="text-[hsl(0,0%,75%)] hover:text-[hsl(0,0%,95%)] transition-colors"
            >
              Terms &amp; Conditions
            </a>
            <span className="text-[hsl(210,20%,25%)]">·</span>
            <a
              href="/privacy"
              className="text-[hsl(0,0%,75%)] hover:text-[hsl(0,0%,95%)] transition-colors"
            >
              Privacy Policy
            </a>
            <span className="text-[hsl(210,20%,25%)]">·</span>
            <span className="text-[hsl(0,0%,65%)]">Global Operations</span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default SharedFooter;
